import { AddTask } from "@mui/icons-material";
import { Box, Button, FormControl, FormLabel, Grid, TextField, Tooltip } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useMessageInputHandlers } from "../../../../App/State/Hooks/MessageDialog/useMessageInput";
import {
  useMessageScheduleHandlers,
  useMessageScheduleState,
} from "../../../../App/State/Hooks/MessageDialog/useMessageSchedule";
import { useDebouncedHandler } from "../../../../App/State/Hooks/useDebouncedHandler";
import { getDateUtc, getNowUtc, toNearestFiveMinutes } from "../../../../MessagingCenter/Utils";
import { ScheduleTimePicker } from "./ScheduleTimePicker";

export const MessageCalendarOptions = ({
  addButtonTooltipText,
  disableAddToQueueAction,
}: {
  addButtonTooltipText?: string;
  disableAddToQueueAction?: boolean;
}) => {
  const { scheduleDateTime, scheduleDateTimeErrorMessage } = useMessageScheduleState();
  const { handleCalendarChange } = useMessageScheduleHandlers();
  const { handleClearInputState } = useMessageInputHandlers();
  const { handleAddMessageToListQueue } = useMessageScheduleHandlers();

  const handleDateInput = useDebouncedHandler(
    (value: dayjs.Dayjs | null, keyboardInputValue?: string | undefined) => {
      let time = value ? value : keyboardInputValue ? dayjs(keyboardInputValue) : null;
      time = time ? dayjs(time) : null;
      if (time?.isValid()) {
        const roundedTime = toNearestFiveMinutes(time);
        handleCalendarChange(roundedTime);
      }
    },
    50,
  );

  const handleAddButtonClick = useDebouncedHandler(() => {
    handleAddMessageToListQueue();
    handleClearInputState();
  }, 50);

  useEffect(() => {
    if (scheduleDateTime === null) {
      handleCalendarChange(getNowUtc());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleDateTime]);

  return (
    <Box sx={{ mb: 2, width: "100%" }}>
      <Grid
        container
        columnSpacing={1}
        alignItems="center"
        sx={{
          position: "relative",
          "& .MuiFormLabel-root": { position: "absolute", bottom: "100%" },
          "& .MuiFormHelperText-root": {
            position: "absolute",
            top: "100%",
          },
        }}
      >
        <Grid item xs={12} sm={4} paddingTop={0}>
          <FormControl
            required
            aria-required
            aria-label="select-send-date"
            fullWidth
            sx={{
              position: "relative",
            }}
          >
            <FormLabel>Date</FormLabel>
            <DatePicker
              value={scheduleDateTime}
              onChange={handleDateInput}
              disablePast
              InputAdornmentProps={{
                id: "open-calendar-button",
              }}
              renderInput={(params) => (
                <TextField
                  id="select-send-date"
                  {...params}
                  error={!!scheduleDateTimeErrorMessage}
                  helperText={scheduleDateTimeErrorMessage}
                  onKeyDown={(e) => {
                    if (e.ctrlKey && e.key === "Enter") {
                      handleAddButtonClick();
                    }
                  }}
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4} paddingTop={0}>
          <ScheduleTimePicker
            scheduledDate={scheduleDateTime ? getDateUtc(scheduleDateTime) : getNowUtc()}
            handleChangeCalendar={handleCalendarChange}
          />
        </Grid>
        <Grid item xs={12} sm={4} paddingTop={0}>
          <Tooltip title={addButtonTooltipText || ""} arrow>
            <Box width={1}>
              <Button
                id="add-message-to-list-button"
                aria-haspopup="true"
                variant="outlined"
                disableElevation
                onClick={handleAddButtonClick}
                onKeyDown={(e) => {
                  if (e.ctrlKey && e.key === "Enter") {
                    handleAddButtonClick();
                  }
                }}
                endIcon={<AddTask />}
                disabled={disableAddToQueueAction}
                fullWidth
                sx={{ py: 1.83 }}
              >
                Add Message To List
              </Button>
            </Box>
          </Tooltip>
        </Grid>
      </Grid>
    </Box>
  );
};
